import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import styles from './Admin.module.css'
import axios from 'axios'
import config from '../../config'
import BulkPhotoUpload from '../../components/BulkPhotoUpload/BulkPhotoUpload'



const Admin: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [models, setModels] = useState<string[]>([])
  const [activeModel, setActiveModel] = useState<string>('recipes')
  const [records, setRecords] = useState<any[]>([])
  const [recordMeta, setRecordMeta] = useState<{ name: string; type: string; nullable: boolean; primary_key: boolean }[]>([])
  const [recordPage, setRecordPage] = useState(1)
  const [recordTotalPages, setRecordTotalPages] = useState(1)
  const [editDraft, setEditDraft] = useState<Record<string, any> | null>(null)
  const [photoModalFor, setPhotoModalFor] = useState<string | null>(null)
  const [photos, setPhotos] = useState<any[]>([])
  const [photosLoading, setPhotosLoading] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadCaption, setUploadCaption] = useState('')
  const [uploadPrimary, setUploadPrimary] = useState(true)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  
  // Model counts state
  const [modelCounts, setModelCounts] = useState<Record<string, number>>({})
  const [countsLoading, setCountsLoading] = useState(false)
  
  // Bulk operations state
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set())
  const [bulkActionOpen, setBulkActionOpen] = useState(false)
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false)
  const [bulkUpdateFields, setBulkUpdateFields] = useState<Record<string, any>>({})

  // Search functionality state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)
  
  // Bulk photo upload state
  const [showBulkPhotoUpload, setShowBulkPhotoUpload] = useState(false)
  

  const isAdmin = useMemo(() => {
    // For now, treat your account as admin. In a fuller flow, we would fetch the user profile (with is_superuser)
    const admin = user?.email === 'jflorence0@gmail.com'
    return admin
  }, [user])



  // Load model list
  useEffect(() => {
    console.log('🔄 Admin useEffect triggered:', { isAdmin, userEmail: user?.email })
    if (!isAdmin) {
      console.log('❌ Not admin, skipping model load')
      return
    }
    console.log('✅ Loading models from backend...')
    axios.get<string[]>(`${config.API_BASE_URL}/admin/models`).then(({ data }) => {
      console.log('📋 Models loaded:', data)
      console.log('🔐 OAuth models in list:', data.filter(m => m.startsWith('oauth_')))
      setModels(data)
    }).catch((error) => {
      console.error('❌ Error loading models:', error)
      setModels([])
    })
  }, [isAdmin])

  // Load model counts
  const fetchModelCounts = async () => {
    if (!isAdmin) return
    try {
      setCountsLoading(true)
      const { data } = await axios.get<Record<string, number>>(`${config.API_BASE_URL}/admin/models/counts`)
      setModelCounts(data)
    } catch (e) {
      console.error('Failed to fetch model counts:', e)
      setModelCounts({})
    } finally {
      setCountsLoading(false)
    }
  }


  useEffect(() => { 
    if (isAdmin) {
      setCountsLoading(true)
      fetchModelCounts()
    }
  }, [isAdmin])

  const loadModel = async (name: string, p = 1) => {
    setActiveModel(name)
    setRecordPage(p)
    setLoading(true)
    setError(null)
    // Clear search when switching models
    setSearchQuery('')
    setSearchResults([])
    setIsSearching(false)
    try {
      const meta = await axios.get(`${config.API_BASE_URL}/admin/${name}/meta`)
      setRecordMeta(meta.data)
      const list = await axios.get(`${config.API_BASE_URL}/admin/${name}?page=${p}&page_size=20`)
      setRecords(list.data.items)
      setRecordTotalPages(Math.max(1, list.data.total_pages || 1))
      // Refresh model counts after loading a model
      await fetchModelCounts()
    } catch (e) {
      setError('Failed to load model data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (isAdmin) loadModel('recipes', 1) }, [isAdmin])

  // Keyboard shortcut for search (Ctrl+F or Cmd+F)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault()
        const searchInput = document.querySelector(`.${styles.searchInput}`) as HTMLInputElement
        if (searchInput) {
          searchInput.focus()
          searchInput.select()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Bulk operations functions
  const handleSelectAll = () => {
    if (selectedRecords.size === records.length) {
      setSelectedRecords(new Set())
    } else {
      const allIds = records.map(r => r[recordMeta.find(c => c.primary_key)?.name || 'id'])
      setSelectedRecords(new Set(allIds))
    }
  }

  const handleSelectRecord = (recordId: string) => {
    const newSelected = new Set(selectedRecords)
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId)
    } else {
      newSelected.add(recordId)
    }
    setSelectedRecords(newSelected)
  }

  const handleBulkDelete = async () => {
    if (selectedRecords.size === 0) return
    
    try {
      const pk = recordMeta.find(c => c.primary_key)?.name || 'id'
      const ids = Array.from(selectedRecords).map(id => records.find(r => r[pk] === id)?.[pk]).filter(Boolean)
      
      await axios.post(`${config.API_BASE_URL}/admin/${activeModel}/bulk-delete`, { ids })
      
      setSelectedRecords(new Set())
      setBulkActionOpen(false)
      await loadModel(activeModel, recordPage)
    } catch (e) {
      console.error('Bulk delete failed', e)
      alert('Bulk delete failed')
    }
  }

  const handleBulkUpdate = async () => {
    if (selectedRecords.size === 0 || Object.keys(bulkUpdateFields).length === 0) return
    
    try {
      const pk = recordMeta.find(c => c.primary_key)?.name || 'id'
      const ids = Array.from(selectedRecords).map(id => records.find(r => r[pk] === id)?.[pk]).filter(Boolean)
      
      // Clean up empty strings for datetime fields
      const cleanedFields = { ...bulkUpdateFields }
      recordMeta.forEach(field => {
        if (field.type?.toLowerCase().includes('datetime') || field.type?.toLowerCase().includes('timestamp')) {
          if (cleanedFields[field.name] === '') {
            cleanedFields[field.name] = null
          }
        }
      })
      
      await axios.post(`${config.API_BASE_URL}/admin/${activeModel}/bulk-update`, { 
        ids, 
        updates: cleanedFields 
      })
      
      setSelectedRecords(new Set())
      setBulkUpdateOpen(false)
      setBulkUpdateFields({})
      await loadModel(activeModel, recordPage)
    } catch (e) {
      console.error('Bulk update failed', e)
      alert('Bulk update failed')
    }
  }

  // Photo management functions
  const fetchRecipePhotos = async (recipeId: string) => {
    try {
      setPhotosLoading(true)
      const { data } = await axios.get(`${config.API_BASE_URL}/api/recipes/${recipeId}/photos`)
      setPhotos(data)
    } catch (e) {
      console.error('Failed to fetch photos:', e)
      setPhotos([])
    } finally {
      setPhotosLoading(false)
    }
  }

  const openPhotoModal = (recipeId: string) => {
    setPhotoModalFor(recipeId)
    fetchRecipePhotos(recipeId)
  }

  const closePhotoModal = () => {
    setPhotoModalFor(null)
    setPhotos([])
    setUploadFile(null)
    setUploadCaption('')
    setUploadPrimary(true)
    setUploadSuccess(false)
  }

  const deletePhoto = async (photoId: string) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return
    
    try {
      await axios.delete(`${config.API_BASE_URL}/photos/${photoId}`)
      // Refresh photos
      if (photoModalFor) {
        fetchRecipePhotos(photoModalFor)
      }
    } catch (e) {
      console.error('Failed to delete photo:', e)
      alert('Failed to delete photo')
    }
  }

  // Search functionality
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    try {
      // Special handling for recipes - use the proper search API
      if (activeModel === 'recipes') {
        const response = await axios.get(`${config.API_BASE_URL}/api/recipes/search`, {
          params: {
            q: searchQuery,
            page: 1,
            page_size: 100 // Get more results for admin search
          }
        })
        
        // Transform the search results to match the expected format
        const searchResults = response.data.items.map((recipe: any) => ({
          id: recipe.id,
          title: recipe.title,
          created_at: recipe.created_at,
          // Add more fields that might be useful for admin
          source_url: recipe.source_url,
          prep_time: recipe.prep_time,
          cook_time: recipe.cook_time,
          servings: recipe.servings,
          // Include ingredients for better search context
          ingredients: recipe.ingredients?.map((ing: any) => ing.name).join(', ') || ''
        }))
        
        setSearchResults(searchResults)
      } else if (activeModel === 'ingredients' || activeModel === 'ingredient_normalizations' || activeModel === 'ingredient_dietary_attributes') {
        // Use backend search for ingredients, normalized ingredients, and dietary attributes
        const response = await axios.get(`${config.API_BASE_URL}/admin/${activeModel}`, {
          params: {
            search: searchQuery,
            page: 1,
            page_size: 100 // Get more results for admin search
          }
        })
        
        setSearchResults(response.data.items)
      } else {
        // For other models, use client-side search through current records
        const searchableFields = recordMeta.filter(field => {
          const fieldType = field.type?.toLowerCase() || ''
          const fieldName = field.name.toLowerCase()
          
          // Include text fields, IDs, and common searchable fields
          return fieldType.includes('text') || 
                 fieldType.includes('varchar') || 
                 fieldType.includes('string') ||
                 fieldName.includes('id') ||
                 fieldName.includes('name') ||
                 fieldName.includes('title') ||
                 fieldName.includes('text') ||
                 fieldName.includes('description')
        })

        const results = records.filter(record => {
          return searchableFields.some(field => {
            const value = String(record[field.name] || '').toLowerCase()
            return value.includes(searchQuery.toLowerCase())
          })
        })

        setSearchResults(results)
      }
    } catch (error) {
      console.error('Search failed:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults([])
    setIsSearching(false)
  }

  // Highlight search terms in text
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    return text.replace(regex, '<mark>$1</mark>')
  }

  if (!user) {
    return (
      <div className={styles.container}>
        <div className={styles.adminHeader}><div className={styles.brand}>PLATEFULL Admin</div></div>
        <div className={styles.content}><p>Please sign in.</p></div>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className={styles.container}>
        <div className={styles.adminHeader}><div className={styles.brand}>PLATEFULL Admin</div></div>
        <div className={styles.content}><p>Admin access required.</p></div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.adminHeader}>
        <div className={styles.brand}>PLATEFULL Admin</div>
        <div className={styles.adminActions}>
          <button className={styles.button} onClick={logout}>Log out</button>
        </div>
      </div>
      <div className={styles.content}>
        <div className={styles.adminLayout}>
          <aside className={styles.sidebar}>
            <div className={styles.sectionLabel}>Machine Learning</div>
            <ul className={styles.modelList}>
              <li>
                <button 
                  className={styles.modelLink}
                  onClick={() => navigate('/admin/ml')}
                >
                  ML Admin Dashboard
                  <span className={styles.mlBadge}>🤖</span>
                </button>
              </li>
            </ul>

            <div className={styles.sectionLabel}>Content Management</div>
            <ul className={styles.modelList}>
              <li>
                <button 
                  className={styles.modelLink}
                  onClick={() => navigate('/admin/items')}
                >
                  Favorites Items
                  <span className={styles.mlBadge}>⭐</span>
                </button>
              </li>
            </ul>

            <div className={styles.sectionLabel}>Models</div>
            <ul className={styles.modelList}>
              {models.map(m => (
                <li key={m}>
                  <button className={`${styles.modelLink} ${activeModel===m ? styles.active : ''}`} onClick={() => loadModel(m, 1)}>
                    {m} <span className={styles.modelCount}>{countsLoading ? '...' : (modelCounts[m] || 0)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <main className={styles.mainPanel}>
            <div className={styles.panelHeader}>
              <h3 className={styles.panelTitle}>
                {activeModel} 
                {modelCounts[activeModel] !== undefined && (
                  <span className={styles.modelCount}>
                    {countsLoading ? '...' : modelCounts[activeModel]}
                  </span>
                )}
              </h3>
            </div>
            {loading && <p>Loading…</p>}
            {error && <p className={styles.error}>{error}</p>}
            
            {!editDraft ? (
              <>
                {/* Search Section */}
                <div className={styles.searchSection}>
                  <div className={styles.searchInputGroup}>
                    <input
                      type="text"
                      placeholder={`Search ${activeModel}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className={styles.searchInput}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button 
                      className={styles.searchButton} 
                      onClick={handleSearch}
                      disabled={isSearching}
                    >
                      {isSearching ? 'Searching...' : 'Search'}
                    </button>
                    <button 
                      className={styles.clearSearchButton} 
                      onClick={clearSearch}
                      disabled={!searchQuery.trim()}
                    >
                      Clear
                    </button>
                  </div>
                  {searchResults.length > 0 && (
                    <div className={styles.searchResultsInfo}>
                      Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className={styles.actionButtonsRow}>
                  <button className={styles.button} onClick={() => loadModel(activeModel, recordPage)}>Refresh</button>
                  <button className={styles.button} onClick={fetchModelCounts} disabled={countsLoading}>
                    {countsLoading ? 'Refreshing...' : 'Refresh Counts'}
                  </button>
                  <button className={styles.button} onClick={() => setEditDraft({})}>Add {activeModel.slice(0, -1)}</button>
                  {activeModel === 'recipes' && (
                    <button 
                      className={`${styles.button} ${styles.primaryButton}`}
                      onClick={() => setShowBulkPhotoUpload(true)}
                    >
                      📸 Upload Missing Photos
                    </button>
                  )}
                  {activeModel === 'dietary_attributes' && (
                    <button 
                      className={styles.button} 
                      onClick={async () => {
                        try {
                          await axios.post(`${config.API_BASE_URL}/admin/dietary-attributes/seed`)
                          alert('Successfully seeded common dietary attributes!')
                          loadModel(activeModel, recordPage)
                          // Also refresh counts after seeding
                          await fetchModelCounts()
                        } catch (err) {
                          console.error('Seeding failed', err)
                          alert('Failed to seed dietary attributes. They may already exist.')
                        }
                      }}
                    >
                      Seed Common Attributes
                    </button>
                  )}
                  {activeModel === 'ingredient_dietary_attributes' && (
                    <button 
                      className={`${styles.button} ${styles.primaryButton}`}
                      onClick={() => navigate('/ml-admin')}
                    >
                    </button>
                  )}
                  {selectedRecords.size > 0 && (
                    <div className={styles.bulkActions}>
                      <span className={styles.selectedCount}>{selectedRecords.size} selected</span>
                      <button 
                        className={styles.button} 
                        onClick={() => setBulkActionOpen(true)}
                      >
                        Bulk Actions
                      </button>
                    </div>
                  )}
                </div>
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>
                          <input 
                            type="checkbox" 
                            checked={selectedRecords.size === (searchResults.length > 0 ? searchResults.length : records.length) && (searchResults.length > 0 ? searchResults.length : records.length) > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        {recordMeta.map(c => (<th key={c.name}>{c.name}</th>))}
                        {/* Additional columns for recipe search results */}
                        {activeModel === 'recipes' && searchResults.length > 0 && (
                          <>
                            <th>Ingredients</th>
                          </>
                        )}
                        {activeModel === 'recipes' && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(searchResults.length > 0 ? searchResults : records).map((row, idx) => {
                        const recordId = row[recordMeta.find(c => c.primary_key)?.name || 'id']
                        return (
                          <tr key={idx}>
                            <td>
                              <input 
                                type="checkbox" 
                                checked={selectedRecords.has(recordId)}
                                onChange={() => handleSelectRecord(recordId)}
                              />
                            </td>
                            {recordMeta.map(c => (
                              <td key={c.name} className={c.name==='source_url' ? styles.urlCell : undefined}>
                                {c.name==='source_url' && row[c.name]
                                  ? <a href={row[c.name]} target="_blank" rel="noreferrer">{row[c.name]}</a>
                                  : (c.name === 'id' || c.name === 'title' || c.name === 'name' || c.name === 'text') && searchResults.length > 0 && searchQuery.trim() 
                                    ? <span 
                                        className={styles.clickableField}
                                        onClick={() => setEditDraft(row)}
                                        title="Click to edit this record"
                                        dangerouslySetInnerHTML={{ __html: highlightSearchTerm(String(row[c.name] ?? ''), searchQuery) }} 
                                      />
                                    : (c.name === 'id' || c.name === 'title' || c.name === 'name' || c.name === 'text')
                                      ? <span 
                                          className={styles.clickableField}
                                          onClick={() => setEditDraft(row)}
                                          title="Click to edit this record"
                                        >
                                          {String(row[c.name] ?? '')}
                                        </span>
                                      : String(row[c.name] ?? '')}
                              </td>
                            ))}
                            {/* Show additional fields for recipe search results */}
                            {activeModel === 'recipes' && searchResults.length > 0 && (
                              <>
                                {row.ingredients && (
                                  <td className={styles.ingredientsCell} title={row.ingredients}>
                                    {row.ingredients.length > 100 ? row.ingredients.substring(0, 100) + '...' : row.ingredients}
                                  </td>
                                )}
                              </>
                            )}
                            {/* Actions column for recipes */}
                            {activeModel === 'recipes' && (
                              <td>
                                <button 
                                  className={styles.photoButton}
                                  onClick={() => openPhotoModal(row.id)}
                                  title="Manage photos for this recipe"
                                >
                                  Photos
                                </button>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
                {recordTotalPages > 1 && (
                  <div className={styles.pagination}>
                    <button className={styles.pageButton} disabled={recordPage===1} onClick={() => loadModel(activeModel, recordPage-1)}>Prev</button>
                    <span className={styles.pageInfo}>Page {recordPage} of {recordTotalPages}</span>
                    <button className={styles.pageButton} disabled={recordPage===recordTotalPages} onClick={() => loadModel(activeModel, recordPage+1)}>Next</button>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.editor}>
                <h4 className={styles.panelTitle}>{editDraft && editDraft[recordMeta.find(c=>c.primary_key)?.name || 'id'] ? 'Edit' : 'Create'} {activeModel.slice(0, -1)}</h4>
                <form onSubmit={async (e) => {
                  e.preventDefault()
                  const pk = recordMeta.find(c=>c.primary_key)?.name || 'id'
                  
                  // Clean up empty strings for datetime fields
                  const cleanedDraft = { ...editDraft }
                  recordMeta.forEach(field => {
                    if (field.type?.toLowerCase().includes('datetime') || field.type?.toLowerCase().includes('timestamp')) {
                      if (cleanedDraft[field.name] === '') {
                        cleanedDraft[field.name] = null
                      }
                    }
                  })
                  
                  if (editDraft && editDraft[pk]) {
                    await axios.patch(`${config.API_BASE_URL}/admin/${activeModel}/${editDraft[pk]}`, cleanedDraft)
                  } else {
                    await axios.post(`${config.API_BASE_URL}/admin/${activeModel}`, cleanedDraft)
                  }
                  setEditDraft(null)
                  loadModel(activeModel, recordPage)
                }}>
                  <div className={styles.formGrid}>
                    {recordMeta.filter(c => !c.primary_key && c.name !== 'created_at' && c.name !== 'updated_at').map(c => {
                      // Special handling for dietary attributes
                      if (activeModel === 'dietary_attributes') {
                        if (c.name === 'attribute_type') {
                          return (
                            <label key={c.name} className={styles.formField}>
                              <span>{c.name}</span>
                              <select 
                                className={styles.input} 
                                value={String(editDraft?.[c.name] ?? '')} 
                                onChange={(e)=> setEditDraft(prev => ({ ...(prev||{}), [c.name]: e.target.value }))}
                              >
                                <option value="">Select type...</option>
                                <option value="boolean">Boolean (true/false)</option>
                                <option value="quantity_dependent">Quantity Dependent</option>
                                <option value="numeric">Numeric</option>
                              </select>
                            </label>
                          );
                        }
                        
                        if (c.name === 'description') {
                          return (
                            <label key={c.name} className={styles.formField}>
                              <span>{c.name}</span>
                              <textarea 
                                className={styles.input} 
                                value={String(editDraft?.[c.name] ?? '')} 
                                onChange={(e)=> setEditDraft(prev => ({ ...(prev||{}), [c.name]: e.target.value }))}
                                rows={4}
                                placeholder="Enter detailed description..."
                              />
                            </label>
                          );
                        }
                        
                        if (c.name === 'is_active') {
                          return (
                            <label key={c.name} className={styles.formField}>
                              <span>{c.name}</span>
                              <input 
                                type="checkbox" 
                                className={styles.checkbox} 
                                checked={Boolean(editDraft?.[c.name] ?? true)} 
                                onChange={(e)=> setEditDraft(prev => ({ ...(prev||{}), [c.name]: e.target.checked }))} 
                              />
                            </label>
                          );
                        }
                        
                        if (c.name === 'is_allergen') {
                          return (
                            <label key={c.name} className={styles.formField}>
                              <span>{c.name}</span>
                              <input 
                                type="checkbox" 
                                className={styles.checkbox} 
                                checked={Boolean(editDraft?.[c.name] ?? false)} 
                                onChange={(e)=> setEditDraft(prev => ({ ...(prev||{}), [c.name]: e.target.checked }))} 
                              />
                            </label>
                          );
                        }
                        
                        if (c.name === 'unit') {
                          return (
                            <label key={c.name} className={styles.formField}>
                              <span>{c.name}</span>
                              <select 
                                className={styles.input} 
                                value={String(editDraft?.[c.name] ?? '')} 
                                onChange={(e)=> setEditDraft(prev => ({ ...(prev||{}), [c.name]: e.target.value }))}
                              >
                                <option value="">No unit (for boolean attributes)</option>
                                <option value="grams">grams</option>
                                <option value="mg">mg (milligrams)</option>
                                <option value="cups">cups</option>
                                <option value="tablespoons">tablespoons</option>
                                <option value="teaspoons">teaspoons</option>
                                <option value="ounces">ounces</option>
                                <option value="pounds">pounds</option>
                                <option value="ml">ml (milliliters)</option>
                                <option value="liters">liters</option>
                              </select>
                            </label>
                          );
                        }
                      }
                      
                      // Default input field for other cases
                      return (
                        <label key={c.name} className={styles.formField}>
                          <span>{c.name}</span>
                          <input className={styles.input} value={String(editDraft?.[c.name] ?? '')} onChange={(e)=> setEditDraft(prev => ({ ...(prev||{}), [c.name]: e.target.value }))} />
                        </label>
                      );
                    })}
                  </div>
                  <div className={styles.actions}>
                    <button type="submit" className={styles.button}>Save</button>
                    <button type="button" className={styles.button} onClick={() => setEditDraft(null)}>Cancel</button>
                  </div>
                  {activeModel === 'dietary_attributes' && (
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '1rem', 
                      backgroundColor: '#2a2a2a', 
                      border: '1px solid #444',
                      borderRadius: '6px', 
                      fontSize: '0.9rem',
                      color: '#e0e0e0'
                    }}>
                      <h5 style={{ margin: '0 0 0.75rem 0', color: '#ffffff', fontSize: '1rem' }}>📚 Field Guide</h5>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', lineHeight: '1.5' }}>
                        <li style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#4ade80' }}>Boolean:</strong> Simple true/false (e.g., "vegan", "gluten-free")
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#60a5fa' }}>Quantity Dependent:</strong> Varies by amount (e.g., "low FODMAP" - safe up to X grams)
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#f59e0b' }}>Numeric:</strong> Exact measurements (e.g., "sodium content" in mg)
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#a78bfa' }}>Unit:</strong> Leave empty for boolean attributes, set appropriate unit for others
                        </li>
                        <li style={{ marginBottom: '0.5rem' }}>
                          <strong style={{ color: '#ef4444' }}>Allergen vs Preference:</strong>
                          <ul style={{ marginTop: '0.5rem', paddingLeft: '1rem' }}>
                            <li><strong style={{ color: '#ef4444' }}>Allergen:</strong> Medical requirement (e.g., "gluten-allergy", "nut-allergy")</li>
                            <li><strong style={{ color: '#10b981' }}>Preference:</strong> Dietary choice (e.g., "gluten-free", "vegan")</li>
                          </ul>
                        </li>
                      </ul>
                    </div>
                  )}
                </form>
              </div>
            )}
          </main>
        </div>
      </div>
      {photoModalFor && (
        <div className={styles.modalOverlay} onClick={closePhotoModal}>
          <div className={styles.modalPanel} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h4 className={styles.modalTitle}>Manage Photos</h4>
                <p className={styles.modalSubtitle}>{(() => { const row = records.find(r => (r.id || r[recordMeta.find(c=>c.primary_key)?.name || 'id']) === photoModalFor); return row && row.title ? row.title : '' })()}</p>
                <p className={styles.modalSubtitle}>Recipe ID: {photoModalFor}</p>
              </div>
              <button className={styles.closeX} onClick={closePhotoModal}>×</button>
            </div>
            <div className={`${styles.modalBody} ${styles.modalBodyNarrow}`}>
              <div className={styles.uploader}>
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '1rem', alignItems: 'start' }}>
                  <div>
                    <div className={styles.preview}>
                      {uploadFile ? (<img src={URL.createObjectURL(uploadFile)} alt="preview" />) : (
                        <div className={styles.placeholder} style={{ width: '100%', height: '100%' }}>
                          <div className={styles.placeholderIcon}>📷</div>
                          <div className={styles.placeholderText}>Preview</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gap: '.5rem' }}>
                    <label>Upload image
                      <input type="file" accept="image/*" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
                    </label>
                    <label>Caption
                      <input className={styles.input} value={uploadCaption} onChange={(e)=> setUploadCaption(e.target.value)} />
                    </label>
                    <label className={styles.checkbox}>
                      <input type="checkbox" checked={uploadPrimary} onChange={(e)=> setUploadPrimary(e.target.checked)} />
                      Set as primary
                    </label>
                    <span className={styles.hint}>Primary images show up on cards by default.</span>
                    <div className={styles.actions}>
                      <button className={styles.button} onClick={async ()=>{
                        if(!uploadFile || !photoModalFor) return
                        try {
                          const form = new FormData()
                          form.append('file', uploadFile)
                          form.append('caption', uploadCaption)
                          form.append('is_primary', String(uploadPrimary))
                          await axios.post(
                            `${config.API_BASE_URL}/api/recipes/${photoModalFor}/photos/upload`,
                            form,
                            { headers: { 'Content-Type': 'multipart/form-data' } }
                          )
                          setUploadSuccess(true)
                          // Refresh photos after upload
                          fetchRecipePhotos(photoModalFor)
                          setTimeout(() => {
                            setUploadSuccess(false)
                          }, 800)
                        } catch (err) {
                          console.error('Upload failed', err)
                          alert('Upload failed')
                        }
                      }}>Upload</button>
                      {uploadSuccess && (
                        <span className={styles.successNote}><span className={styles.successIcon}>✓</span> Uploaded</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.photoGrid}>
                {photosLoading ? (
                  <p>Loading photos...</p>
                ) : photos.length === 0 ? (
                  <p className={styles.noPhotos}>No photos uploaded yet</p>
                ) : (
                  photos.map(photo => (
                    <div key={photo.id} className={styles.photoCard}>
                      <img 
                        src={photo.url} 
                        alt={photo.caption || 'Recipe photo'} 
                        className={styles.photoImg}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                      <div className={styles.photoInfo}>
                        {photo.caption && (
                          <p className={styles.photoCaption}>{photo.caption}</p>
                        )}
                        {photo.is_primary && (
                          <span className={styles.primaryBadge}>Primary</span>
                        )}
                        <div className={styles.photoActions}>
                          <button 
                            className={`${styles.button} ${styles.danger}`}
                            onClick={() => deletePhoto(photo.id)}
                            title="Delete photo"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Modal */}
      {bulkActionOpen && (
        <div className={styles.modalOverlay} onClick={() => setBulkActionOpen(false)}>
          <div className={styles.modalPanel} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h4 className={styles.modalTitle}>Bulk Actions</h4>
              <button className={styles.closeX} onClick={() => setBulkActionOpen(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <p>Selected {selectedRecords.size} records</p>
              <div className={styles.bulkActionButtons}>
                <button 
                  className={`${styles.button} ${styles.dangerButton}`}
                  onClick={handleBulkDelete}
                >
                  Delete Selected
                </button>
                <button 
                  className={styles.button}
                  onClick={() => {
                    setBulkActionOpen(false)
                    setBulkUpdateOpen(true)
                  }}
                >
                  Update Selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {bulkUpdateOpen && (
        <div className={styles.modalOverlay} onClick={() => setBulkUpdateOpen(false)}>
          <div className={styles.modalPanel} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h4 className={styles.modalTitle}>Bulk Update</h4>
              <button className={styles.closeX} onClick={() => setBulkUpdateOpen(false)}>×</button>
            </div>
            <div className={styles.modalBody}>
              <p>Update {selectedRecords.size} selected records</p>
              <div className={styles.bulkUpdateForm}>
                {recordMeta
                  .filter(c => !c.primary_key && c.name !== 'created_at' && c.name !== 'updated_at')
                  .map(c => (
                    <label key={c.name} className={styles.formField}>
                      <span>{c.name}</span>
                      <input 
                        className={styles.input} 
                        value={String(bulkUpdateFields[c.name] ?? '')} 
                        onChange={(e) => setBulkUpdateFields(prev => ({ 
                          ...prev, 
                          [c.name]: e.target.value 
                        }))} 
                        placeholder={`Leave empty to skip ${c.name}`}
                      />
                    </label>
                  ))
                }
                <div className={styles.bulkUpdateActions}>
                  <button 
                    className={styles.button}
                    onClick={handleBulkUpdate}
                    disabled={Object.keys(bulkUpdateFields).length === 0}
                  >
                    Update All Selected
                  </button>
                  <button 
                    className={styles.button}
                    onClick={() => {
                      setBulkUpdateOpen(false)
                      setBulkUpdateFields({})
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Photo Upload Modal */}
      <BulkPhotoUpload
        isOpen={showBulkPhotoUpload}
        onClose={() => setShowBulkPhotoUpload(false)}
        onComplete={() => {
          // Refresh the current model to show updated data
          loadModel(activeModel, recordPage)
          setShowBulkPhotoUpload(false)
        }}
      />

    </div>
  )
}

export default Admin


